var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { localized, msg } from '@lit/localize';
import { Marked } from 'marked';
import { DataFrame } from '../../model/DataFrame.js';
import '../table/BasicTable.js';
import '../map/GeojsonMap.js';
const marked = new Marked();
let MessageCard = class MessageCard extends LitElement {
    getMessageText() {
        const type = this.message.type;
        const content = this.message.content;
        if (type === 'message') {
            return content.text || '';
        }
        if (type === 'question') {
            return content.question || '';
        }
        if (type === 'error') {
            return content.error || '';
        }
        return `[${type}]`;
    }
    getQuestionOptions() {
        const type = this.message.type;
        if (type === 'question') {
            return this.message.content.options || [];
        }
        return [];
    }
    getTableDataFrame() {
        const type = this.message.type;
        if (type === 'message') {
            // galigeojs spec types table_data as `null` only; runtime returns Array<Record<string, unknown>> | null
            const content = this.message.content;
            if (content.table && content.table_data) {
                const tableDataArray = content.table_data;
                if (Array.isArray(tableDataArray) && tableDataArray.length > 0) {
                    const columnNames = Object.keys(tableDataArray[0] || {});
                    const columns = columnNames.map((colName, index) => ({
                        name: colName,
                        alias: colName,
                        id: index === 0,
                        type: 'string',
                    }));
                    return new DataFrame(columns, tableDataArray);
                }
            }
        }
        return null;
    }
    getGeoJsonData() {
        const type = this.message.type;
        if (type === 'message') {
            const content = this.message.content;
            if (content.geo_table && content.geo_table_data) {
                return content.geo_table_data;
            }
        }
        return null;
    }
    handleOptionClick(option) {
        this.dispatchEvent(new CustomEvent('question-answer', {
            detail: { option },
            bubbles: true,
            composed: true,
        }));
    }
    renderQuestionContent() {
        const options = this.getQuestionOptions();
        return html `
      <div class="message-card__question">
        <span class="message-card__text">${this.getMessageText()}</span>
        ${options.length > 0
            ? html `
              <div class="message-card__options">
                ${options.map((option) => html `
                    <button
                      class="message-card__option-btn"
                      @click="${() => this.handleOptionClick(option)}"
                    >
                      ${option}
                    </button>
                  `)}
              </div>
            `
            : ''}
      </div>
    `;
    }
    renderContent() {
        const type = this.message.type;
        if (type === 'message') {
            const text = this.getMessageText();
            const htmlContent = marked.parse(text);
            const tableDataFrame = this.getTableDataFrame();
            if (tableDataFrame) {
                return html `
          <div class="message-card__with-table">
            <div class="message-card__markdown">${unsafeHTML(htmlContent)}</div>
          </div>
        `;
            }
            return html `<div class="message-card__markdown">${unsafeHTML(htmlContent)}</div>`;
        }
        return html `<span class="message-card__text">${this.getMessageText()}</span>`;
    }
    renderDiscreteContent() {
        const type = this.message.type;
        const content = this.message.content;
        if (type === 'action') {
            const actionContent = content;
            const actionName = actionContent.action_name || '';
            return html `<span class="message-card__discrete-label">${msg('Action:', { id: 'messageCard.label.action', desc: 'Discrete label prefix for an agent action chat message' })}</span> ${actionName}`;
        }
        if (type === 'agent') {
            const agentContent = content;
            const information = agentContent.information || '';
            return html `<span class="message-card__discrete-label">${msg('Agent:', { id: 'messageCard.label.agent', desc: 'Discrete label prefix for an agent information chat message' })}</span> ${information}`;
        }
        if (type === 'information') {
            const infoContent = content;
            const info = infoContent.info || '';
            const details = infoContent.details || '';
            return html `${info}${details ? ` - ${details}` : ''}`;
        }
        if (type === 'error') {
            const errorContent = content;
            const error = errorContent.error || '';
            const details = errorContent.details || '';
            return html `<span class="message-card__discrete-label">${msg('Error:', { id: 'messageCard.label.error', desc: 'Discrete label prefix for an error chat message' })}</span> ${error}${details ? ` - ${details}` : ''}`;
        }
        return html `<span>${this.getMessageText()}</span>`;
    }
    renderTableCard() {
        const tableDataFrame = this.getTableDataFrame();
        if (!tableDataFrame)
            return '';
        return html `
        <basic-table class="message-card__table-card"
          .dataFrame=${tableDataFrame}
          .rowsPerPage=${5}
          selectType="none"
        ></basic-table>
    `;
    }
    renderGeoJsonMap() {
        const geoJsonData = this.getGeoJsonData();
        if (!geoJsonData)
            return '';
        return html `
      <geojson-map
        class="message-card__map"
        .geojson=${geoJsonData}
        .mapOptions=${{ style: 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json' }}
      ></geojson-map>
    `;
    }
    render() {
        const type = this.message.type;
        // Question type renders with card and interactive buttons
        if (type === 'question') {
            return html `
        <basic-card size="small">
          <div class="message-card">
            <span class="message-card__author">${this.message.created_by}:</span>
            ${this.renderQuestionContent()}
          </div>
        </basic-card>
      `;
        }
        // Action, agent and information types render as discrete gray text without card
        if (type === 'action' || type === 'agent' || type === 'information') {
            return html `
        <div class="message-card message-card--discrete">
          <span class="message-card__author">${this.message.created_by}:</span>
          <span class="message-card__discrete-content">${this.renderDiscreteContent()}</span>
        </div>
      `;
        }
        // Error type renders as discrete red text without card
        if (type === 'error') {
            return html `
        <div class="message-card message-card--discrete message-card--error">
          <span class="message-card__author">${this.message.created_by}:</span>
          <span class="message-card__discrete-content">${this.renderDiscreteContent()}</span>
        </div>
      `;
        }
        // Message type renders with full card styling
        const hasTable = this.getTableDataFrame() !== null;
        const hasGeoJson = this.getGeoJsonData() !== null;
        if (hasTable || hasGeoJson) {
            return html `
        <div class="message-card__container">
          <basic-card size="small">
            <div class="message-card">
              <span class="message-card__author">${this.message.created_by}:</span>
              ${this.renderContent()}
            </div>
          </basic-card>
          ${this.renderTableCard()}
          ${this.renderGeoJsonMap()}
        </div>
      `;
        }
        return html `
      <basic-card size="small">
        <div class="message-card">
          <span class="message-card__author">${this.message.created_by}:</span>
          ${this.renderContent()}
        </div>
      </basic-card>
    `;
    }
};
MessageCard.styles = css `
    :host {
      display: block;
    }

    .message-card {
      display: flex;
      gap: 8px;
      font-size: 0.875rem;
      line-height: 1.4;
    }

    .message-card__author {
      font-weight: 600;
      color: #333;
      white-space: nowrap;
    }

    .message-card__text {
      color: #444;
      white-space: pre-wrap;
      word-break: break-word;
    }

    /* Discrete styles for non-message types */
    .message-card--discrete {
      color: #888;
      font-size: 0.8rem;
      padding: 4px 0;
    }

    .message-card--discrete .message-card__author {
      color: #999;
      font-weight: 500;
    }

    .message-card--discrete .message-card__text {
      color: #888;
    }

    .message-card__discrete-content {
      display: inline;
    }

    .message-card__discrete-label {
      font-style: italic;
    }

    /* Question styles */
    .message-card__question {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .message-card__options {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-top: 8px;
    }

    .message-card__option-btn {
      padding: 6px 12px;
      border: 1px solid #0070d2;
      border-radius: 4px;
      background: #fff;
      color: #0070d2;
      cursor: pointer;
      font-size: 0.875rem;
      transition: background-color 0.2s, color 0.2s;
    }

    .message-card__option-btn:hover {
      background: #0070d2;
      color: #fff;
    }

    /* Markdown styles */
    .message-card__markdown {
      color: #444;
      word-break: break-word;
    }

    .message-card__markdown p {
      margin: 0 0 0.5em 0;
    }

    .message-card__markdown p:last-child {
      margin-bottom: 0;
    }

    .message-card__markdown code {
      background: #f4f4f4;
      padding: 0.2em 0.4em;
      border-radius: 3px;
      font-family: 'Consolas', 'Monaco', monospace;
      font-size: 0.85em;
    }

    .message-card__markdown pre {
      background: #f4f4f4;
      padding: 12px;
      border-radius: 4px;
      overflow-x: auto;
      margin: 0.5em 0;
    }

    .message-card__markdown pre code {
      background: none;
      padding: 0;
    }

    .message-card__markdown table {
      border-collapse: collapse;
      width: 100%;
      margin: 0.5em 0;
    }

    .message-card__markdown th,
    .message-card__markdown td {
      border: 1px solid #ddd;
      padding: 8px;
      text-align: left;
    }

    .message-card__markdown th {
      background: #f4f4f4;
      font-weight: 600;
    }

    .message-card__markdown ul,
    .message-card__markdown ol {
      margin: 0.5em 0;
      padding-left: 1.5em;
    }

    .message-card__markdown blockquote {
      border-left: 3px solid #ddd;
      margin: 0.5em 0;
      padding-left: 1em;
      color: #666;
    }

    .message-card__markdown a {
      color: #0070d2;
      text-decoration: none;
    }

    .message-card__markdown a:hover {
      text-decoration: underline;
    }

    .message-card__markdown strong {
      font-weight: 600;
    }

    .message-card__markdown em {
      font-style: italic;
    }

    .message-card__container {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .message-card__table-card {
      width: 100%;
    }

    .message-card__with-table {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .message-card__map {
      width: 100%;
      height: 300px;
      border-radius: 4px;
      overflow: hidden;
    }

    /* Error styles - discrete red text */
    .message-card--error,
    .message-card--error .message-card__author,
    .message-card--error .message-card__discrete-content,
    .message-card--error .message-card__discrete-label {
      color: #c23934;
    }
  `;
__decorate([
    property({ type: Object })
], MessageCard.prototype, "message", void 0);
MessageCard = __decorate([
    localized(),
    customElement('message-card')
], MessageCard);
export { MessageCard };
//# sourceMappingURL=MessageCard.js.map